// Hideo - Hooks page (Tools › Hooks): hooks, opening lines and prompts, stored in data.vault (never rename it), with
// per-project tags (src/features/tags.ts). The page is src/pages/HooksPage.vue, the dialog src/components/dialogs/HookDialog.vue.
import { hasTag } from '../data/tags'
import type { Hook, ProfileData, Project } from '../data/types'
import { barTagsOf, countWithTag, groupByTag, projectsWithTag, type TagGroupOf } from './tags'
import { ALL_PROJECTS, ofProject, validProject } from './projects'
import { putItem, removeItem } from '../data/items'

/** Hooks of the active project ('all' = every hook) */
export const projectHooks = (d: ProfileData, active: string): Hook[] => ofProject(d.vault, active)

interface HookChip {
  tag: string
  count: number
  /** Projects that have the tag: shown as dots only with "All projects" */
  projects: Project[]
}

interface HooksView {
  /** The hooks of the active project that match the search */
  hooks: Hook[]
  chips: HookChip[]
  /** Filter actually applied: a tag no longer in the bar counts as "All" */
  filter: string
  groups: TagGroupOf<Hook>[]
}

/** Text of a hook as the search reads it; the page passes mentionPlain (mentions read as their names: src/features/mentions.ts,
 *  not imported here because it needs the stores) */
type PlainText = (text: string) => string

/** A hook matches the search when its title or its text contains it, ignoring case */
export function hookMatches(h: Hook, query: string, plain: PlainText = s => s): boolean {
  const q = query.trim().toLowerCase()
  return !q || (h.title || '').toLowerCase().includes(q) || plain(h.content || '').toLowerCase().includes(q)
}

/** Everything the Hooks page shows, for the active project, the tag chosen in the bar and the search (counts follow it;
 *  while searching, the sections without a match are left out) */
export function hooksView(d: ProfileData, active: string, filter: string, query = '', plain?: PlainText): HooksView {
  const searching = !!query.trim()
  const hooks = projectHooks(d, active).filter(h => hookMatches(h, query, plain)), tags = barTagsOf(d, 'hook', active)
  const f = filter && hasTag(tags, filter) ? filter : ''
  const groups = groupByTag(hooks, tags, f)
  return {
    hooks,
    chips: tags.map(tag => ({ tag, count: countWithTag(hooks, tag), projects: active === ALL_PROJECTS ? projectsWithTag(d, 'hook', tag) : [] })),
    filter: f,
    groups: searching ? groups.filter(g => g.items.length) : groups
  }
}

// ===== Hook dialog =====
/** The fields of the hook dialog */
interface HookForm {
  title: string
  tags: string[]
  projectId: string
  /** With @mentions as "@[type:id]" (src/features/mentions.ts) */
  content: string
}

/** The dialog's starting fields: those of the hook, or empty for a new one, with the tag chosen on the Hooks page
 *  (filter) and the project of new items (newProject) */
export function hookForm(d: ProfileData, id: string | null, filter: string, newProject: string): HookForm {
  const h = id ? d.vault.find(x => x.id === id) : null
  if (h) return { title: h.title || '', tags: [...(h.tags || [])], projectId: validProject(d, h.projectId, newProject), content: h.content || '' }
  return { title: '', tags: filter ? [filter] : [], projectId: newProject, content: '' }
}

/** Saves the dialog into the data: a new hook (id null) or the hook with that id, replaced by the dialog's fields (title
 *  and content trimmed). null when the title is empty (nothing changes). created = a new hook. The caller saves. */
export function saveHook(d: ProfileData, id: string | null, f: HookForm, now = Date.now()): { hook: Hook; created: boolean } | null {
  const title = f.title.trim()
  if (!title) return null
  const hook: Hook = { id: id || 'vt_' + now, title, tags: [...f.tags], projectId: f.projectId, content: f.content.trim() }
  return { hook, created: putItem(d.vault, hook, !!id) }
}

/** Removes a hook from the data (true if it was there). The caller saves. */
export function deleteHook(d: ProfileData, id: string): boolean {
  return removeItem(d, 'vault', id)
}
